import React, { useRef, useState } from "react";
import { Camera, ImagePlus, Radio } from "lucide-react";
import { t as T } from "@/lib/i18n";
import { LivePhotoCaptureDialog } from "@/components/LivePhotoCaptureDialog";
import { livePhotoUploadAccept } from "@/lib/applePhotoImport";

// Extract pasted images (Ctrl+V a screenshot/picture) from a clipboard event.
export const filesFromPaste = (e) =>
  Array.from(e.clipboardData?.items || [])
    .filter((i) => i.type?.startsWith("image/"))
    .map((i) => i.getAsFile())
    .filter(Boolean);

// onPaste handler factory: attaches pasted pictures via the given addFiles().
export const pasteToFiles = (addFiles) => (e) => {
  const fs = filesFromPaste(e);
  if (fs.length) {
    e.preventDefault();
    addFiles(fs);
  }
};

// Phones/tablets get a dedicated Camera button (Android galleries often hide
// the camera); desktops only get Upload.
const isTouchDevice = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(pointer: coarse)")?.matches ||
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || ""));

// Android switches its picker from the photo Gallery (camera in the corner) to
// the generic Files app the moment non-media extensions (.livp/.zip) appear in
// `accept` — so only iOS (whose picker always offers Photo Library / Take
// Photo / Choose File) gets the Live-Photo package types.
const GALLERY_ACCEPT = "image/*,video/quicktime,video/mp4,video/webm";

export default function PhotoAddButtons({ onFiles, testidPrefix = "photo" }) {
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);
  const [liveOpen, setLiveOpen] = useState(false);
  const showCamera = isTouchDevice();
  const handle = (e) => {
    onFiles(e.target.files);
    e.target.value = "";
  };
  return (
    <>
      <input
        ref={galleryRef}
        type="file"
        accept={livePhotoUploadAccept(GALLERY_ACCEPT)}
        multiple
        className="hidden"
        onChange={handle}
        data-testid={`${testidPrefix}-file-input`}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handle}
        data-testid={`${testidPrefix}-native-camera-input`}
      />
      <button
        type="button"
        onClick={() => galleryRef.current?.click()}
        className="w-14 h-14 border border-dashed border-border rounded-sm flex flex-col items-center justify-center gap-0.5 text-muted-foreground hover:text-foreground hover:border-foreground"
        title="Upload photos from gallery"
        data-testid={`${testidPrefix}-upload-btn`}
      >
        <ImagePlus className="w-5 h-5" />
        <span className="text-[9px] font-medium leading-none">{T("Upload")}</span>
      </button>
      {showCamera && (
        <button
          type="button"
          onClick={() => cameraRef.current?.click()}
          className="w-14 h-14 border border-border rounded-sm flex flex-col items-center justify-center gap-0.5 text-foreground hover:border-foreground hover:bg-secondary"
          title="Take a regular photo with the phone camera"
          data-testid={`${testidPrefix}-camera-btn`}
        >
          <Camera className="w-5 h-5" />
          <span className="text-[9px] font-bold leading-none">{T("Camera")}</span>
        </button>
      )}
      <button
        type="button"
        onClick={() => setLiveOpen(true)}
        className="w-14 h-14 border border-emerald-500 bg-emerald-50 rounded-sm flex flex-col items-center justify-center gap-0.5 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-600"
        title="Capture a Live Photo (records motion right here)"
        data-testid={`${testidPrefix}-live-btn`}
      >
        <Radio className="w-5 h-5" />
        <span className="text-[9px] font-bold leading-none">LIVE</span>
      </button>
      {showCamera && (
        <p
          className="basis-full text-[10px] leading-tight text-muted-foreground mt-0.5"
          data-testid={`${testidPrefix}-live-hint`}
        >
          <span className="font-semibold">{T("Camera")}</span> takes a regular photo. Tap{" "}
          <span className="font-semibold text-emerald-700">LIVE</span> to record a Live photo
          (motion + still) — it works even without internet and uploads later.
        </p>
      )}
      <LivePhotoCaptureDialog open={liveOpen} onOpenChange={setLiveOpen} onCaptured={onFiles} testId={`${testidPrefix}-live-capture`} />
    </>
  );
}
